import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import apiManager from '../../../lib/api';

const SetPasswordPage = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState<string | null>(null);

    // Validar token al cargar
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Token no proporcionado');
                setIsValidating(false);
                return;
            }

            try {
                // Usar apiManager para evitar problemas de URL duplicada
                const response = await apiManager.auth.validateResetToken(token);

                if (response.success && response.data?.valid) {
                    setTokenValid(true);
                    if (response.data.email) {
                        setEmail(response.data.email);
                    }
                } else {
                    setError('El enlace ha expirado o no es válido. Por favor, solicita uno nuevo.');
                }
            } catch {
                setError('Error al validar el enlace. Por favor, inténtalo de nuevo.');
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validaciones
        if (password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiManager.auth.resetPassword(token!, password);

            if (response.success) {
                setSuccess(true);
                // Redirigir al login después de 3 segundos
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                const errorMessage = typeof response.error === 'object' ? response.error.message : response.error;
                setError(errorMessage || 'Error al establecer la contraseña');
            }
        } catch {
            setError('Error de conexión. Por favor, inténtalo de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    // Estilos basados en Home y Header
    // Fondo similar a la sección de ventajas (Cream/Beige)
    const pageBackground = "min-h-screen bg-[#f2ede4] flex flex-col";



    // Botón estilo Jarad (Negro -> Amarillo al hover)
    const buttonClass = "w-full py-4 px-6 rounded-xl font-bold text-lg tracking-wide transition-all duration-300 flex items-center justify-center gap-2 group shadow-lg " +
        (isLoading
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-[#0d0d0d] text-white hover:bg-[#f6cf02] hover:text-[#0d0d0d] hover:shadow-xl");

    // Loading estado
    if (isValidating) {
        return (
            <div className={pageBackground}>

                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="text-center">
                        <Loader2 className="h-12 w-12 text-[#f6cf02] animate-spin mx-auto mb-4" />
                        <p className="text-[#0d0d0d] font-medium tracking-wide">Validando seguridad...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Token inválido
    if (!tokenValid && !success) {
        return (
            <div className={pageBackground}>
                <Helmet>
                    <title>Enlace Inválido | Jarad</title>
                </Helmet>


                <div className="flex-1 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100"
                    >
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#0d0d0d] mb-2 font-display">Enlace no válido</h2>
                        <p className="text-gray-500 mb-8 font-light">{error}</p>
                        <Link
                            to="/forgot-password"
                            className="inline-flex items-center justify-center w-full bg-[#f6cf02] text-[#0d0d0d] px-6 py-3 rounded-xl font-bold hover:bg-[#ffe033] transition-colors duration-200 shadow-md"
                        >
                            Solicitar nuevo enlace
                        </Link>
                    </motion.div>
                </div>
            </div>
        );
    }

    // Éxito
    if (success) {
        return (
            <div className={pageBackground}>
                <Helmet>
                    <title>Contraseña Establecida | Jarad</title>
                </Helmet>


                <div className="flex-1 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100"
                    >
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#0d0d0d] mb-2 font-display">¡Bienvenido a la familia!</h2>
                        <p className="text-gray-500 mb-8 font-light leading-relaxed">
                            Tu contraseña ha sido configurada correctamente.<br />
                            Redirigiendo al acceso seguro...
                        </p>
                        <div className="w-full bg-gray-100 rounded-full h-1 mt-4 overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 3 }}
                                className="h-full bg-[#f6cf02]"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    // Formulario principal
    return (
        <div className={pageBackground}>
            <Helmet>
                <title>Establecer Contraseña | Jarad</title>
            </Helmet>



            <div className="flex-1 flex items-start justify-center p-4 pt-12 md:pt-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-md w-full relative z-10"
                >
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
                        <div className="mb-8 text-center">
                            <h1 className="text-2xl font-black text-[#0d0d0d] mb-2 font-display uppercase tracking-tight">Establecer Contraseña</h1>
                            {email && <p className="text-gray-500 text-sm">Cuenta: <span className="font-medium text-[#0d0d0d]">{email}</span></p>}
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-3 border border-red-100"
                                >
                                    <XCircle className="w-5 h-5 flex-shrink-0" />
                                    {error}
                                </motion.div>
                            )}

                            <div className="space-y-5">
                                <div>
                                    <label htmlFor="password" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                        Nueva contraseña
                                    </label>
                                    <input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl text-[#0d0d0d] placeholder-gray-400 focus:ring-2 focus:ring-[#f6cf02] focus:border-transparent transition-all outline-none"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                        Confirmar contraseña
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl text-[#0d0d0d] placeholder-gray-400 focus:ring-2 focus:ring-[#f6cf02] focus:border-transparent transition-all outline-none"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={buttonClass}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="animate-spin h-5 w-5" />
                                            <span>Procesando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Guardar Acceso</span>
                                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    <p className="mt-8 text-center text-sm text-gray-500 font-medium">
                        ¿Ya tienes cuenta?{' '}
                        <Link to="/login" className="text-[#0d0d0d] hover:text-[#f6cf02] transition-colors font-bold underline decoration-[#f6cf02] underline-offset-4 decoration-2">
                            Inicia sesión
                        </Link>
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default SetPasswordPage;
